import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Box,
    Calendar,
    Signature,
    CheckCircle2,
    FileText,
    Image as ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';
import { Dialog, DialogContent } from '../../components/ui/dialog';

const ViewInspection: React.FC = () => {
    const { id, inspectionId } = useParams<{ id: string; inspectionId: string }>();
    const navigate = useNavigate();

    const [asset, setAsset] = useState<Asset | null>(null);
    const [inspection, setInspection] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch asset
                const [assetRes, inspectionsRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<any[]>(`/api/assets/${id}/inspections`)
                ]);

                setAsset(assetRes);

                const foundInspection = inspectionsRes.find(i => i.id === parseInt(inspectionId || '0'));
                if (foundInspection) {
                    setInspection(foundInspection);
                } else {
                    toast.error('Inspection not found.');
                    navigate(`/assets/${id}`);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load inspection details');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, inspectionId, navigate]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!asset || !inspection) {
        return null;
    }

    const { itemResults } = inspection;
    const totalQuestions = itemResults ? itemResults.length : 0;
    const yesCount = itemResults ? itemResults.filter((r: any) => r.answer === 'Yes').length : 0;
    const noCount = itemResults ? itemResults.filter((r: any) => r.answer === 'No').length : 0;
    const naCount = itemResults ? itemResults.filter((r: any) => r.answer === 'N/A').length : 0;
    const answeredCount = itemResults ? itemResults.filter((r: any) => r.answer).length : 0;
    const completionPercentage = totalQuestions === 0 ? 0 : Math.round((answeredCount / totalQuestions) * 100);

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            Inspection Details
                        </h1>
                        <p className="text-gray-500 text-sm font-medium mt-0.5">
                            Viewing completed inspection for {asset.name}
                        </p>
                    </div>
                </div>
                <div className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-bold rounded-lg border border-gray-200 uppercase tracking-widest">
                    Read Only
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                    {/* 1. Asset & Inspector Details */}
                    <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <Box size={24} className="text-blue-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Inspection Details</h2>
                                <p className="text-gray-500 text-xs font-medium">Core information</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-6">
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Name</span>
                                <p className="font-bold text-gray-900">{asset.name} ({asset.tag})</p>
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Status</span>
                                <p className="font-bold text-gray-900">{asset.status}</p>
                            </div>
                            <div className="col-span-2">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Inspection Template</span>
                                <p className="font-bold text-gray-900">{inspection.template?.name || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">Inspection Date</label>
                                <div className="rounded-xl h-12 flex items-center px-4 border border-gray-200 bg-gray-50 text-gray-800 font-medium">
                                    {toSydneyDate(inspection.inspectionDate)}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">Time of Inspection Completion</label>
                                <div className="rounded-xl h-12 flex items-center px-4 border border-gray-200 bg-gray-50 text-gray-800 font-medium">
                                    {inspection.inspectionTime || 'N/A'}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">Inspector Name</label>
                                <div className="rounded-xl h-12 flex items-center px-4 border border-gray-200 bg-gray-50 text-gray-800 font-medium">
                                    {inspection.inspectorName}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">Location (Suburb)</label>
                                <div className="rounded-xl h-12 flex items-center px-4 border border-gray-200 bg-gray-50 text-gray-800 font-medium">
                                    {inspection.suburb || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 2. Checklist */}
                    <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <FileText size={24} className="text-indigo-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Checklist Findings</h2>
                                <p className="text-gray-500 text-xs font-medium">Questions and answers recorded</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {inspection.itemResults?.sort((a: any, b: any) => (a.templateItem?.listOrder || 0) - (b.templateItem?.listOrder || 0)).map((result: any, index: number) => {
                                const isMandatory = result.templateItem?.isMandatory;
                                const answer = result.answer;
                                return (
                                    <div key={result.id} className={`p-5 rounded-2xl border ${isMandatory ? 'bg-blue-50/40 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
                                        <div className="flex justify-between items-start gap-4 mb-4">
                                            <div>
                                                <h4 className="font-bold text-gray-900 text-sm">{index + 1}. {result.templateItem?.question}</h4>
                                                {isMandatory && <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mt-1 block">Mandatory</span>}
                                            </div>
                                            <div className="shrink-0">
                                                <div className={`px-4 py-1.5 text-sm font-bold rounded-lg ${answer === 'Yes' ? 'bg-emerald-100 text-emerald-700' :
                                                    answer === 'No' ? 'bg-red-100 text-red-700' :
                                                        'bg-gray-200 text-gray-700'
                                                    }`}>
                                                    {answer || 'Unanswered'}
                                                </div>
                                            </div>
                                        </div>
                                        {result.comments && (
                                            <div className="mt-3 p-3 bg-white/60 border border-gray-100 rounded-xl text-sm text-gray-600">
                                                <span className="font-bold text-gray-800 mr-2 text-xs uppercase tracking-wider">Notes:</span>
                                                {result.comments}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                            {!itemResults || itemResults.length === 0 && (
                                <div className="text-center py-6 bg-gray-50 rounded-2xl border border-gray-100">
                                    <p className="text-sm font-medium text-gray-500">No questions recorded.</p>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* 3. Photos */}
                    {inspection.photos && inspection.photos.length > 0 && (
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <ImageIcon size={24} className="text-gray-900" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Photos</h2>
                                    <p className="text-gray-500 text-xs font-medium">Uploaded documented photos</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-4 pt-2">
                                {inspection.photos.map((photo: any, idx: number) => (
                                    <div
                                        key={idx}
                                        className="relative w-32 h-32 rounded-xl overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer shadow-sm group"
                                        onClick={() => {
                                            setSelectedImageUrl(photo.photoUrl || photo.url);
                                            setIsImageModalOpen(true);
                                        }}
                                    >
                                        <img
                                            src={photo.photoUrl || photo.url}
                                            alt={`Inspection Photo ${idx + 1}`}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 4. Finalization & Signatures */}
                    <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Signature size={24} className="text-gray-900" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Finalize</h2>
                                <p className="text-gray-500 text-xs font-medium">Outcome and signature</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">Inspection Outcome</label>
                                <div className={`rounded-xl h-12 flex items-center px-4 border border-gray-200 font-bold ${inspection.outcome === 'Pass' ? 'bg-emerald-50 text-emerald-700' :
                                    inspection.outcome === 'Pass with Notes' ? 'bg-amber-50 text-amber-700' :
                                        inspection.outcome === 'Fail' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                                    }`}>
                                    {inspection.outcome || 'N/A'}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">General Comments</label>
                                <div className="rounded-xl p-3 min-h-[48px] border border-gray-200 bg-gray-50 text-gray-800 text-sm">
                                    {inspection.generalComments || <span className="text-gray-400 italic">No comments</span>}
                                </div>
                            </div>
                        </div>

                        {/* Inspector Signature */}
                        <div className="space-y-4 pt-4">
                            <label className="text-xs font-bold text-gray-900 uppercase tracking-wide block">Inspector Signature</label>
                            <div className="border border-gray-200 rounded-2xl p-6 bg-gray-50 flex flex-col items-center justify-center min-h-[160px]">
                                {inspection.inspectorSignature?.startsWith('data:image') ? (
                                    <img src={inspection.inspectorSignature} alt="Inspector Signature" className="max-h-32" />
                                ) : (
                                    <div className="font-[signature] text-4xl tracking-wider text-gray-800">
                                        {inspection.inspectorSignature || 'No Signature'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </div>

                {/* Info Sidebar */}
                <div className="hidden lg:block lg:col-span-1">
                    <div className="sticky top-24 space-y-6">
                        {/* Inspection Summary */}
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-xl shadow-gray-200/50 space-y-6">
                            <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                <Box size={16} className="text-blue-500" />
                                Inspection Summary
                            </h3>

                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-xs font-bold text-gray-600">
                                    <span>Progress</span>
                                    <span>{answeredCount} / {totalQuestions} ({completionPercentage}%)</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${completionPercentage}%` }}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100 flex flex-col items-center justify-center text-center">
                                    <span className="text-2xl font-bold text-emerald-600">{yesCount}</span>
                                    <span className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-wider mt-1">Yes</span>
                                </div>
                                <div className="p-4 bg-red-50/50 rounded-2xl border border-red-100 flex flex-col items-center justify-center text-center">
                                    <span className="text-2xl font-bold text-red-600">{noCount}</span>
                                    <span className="text-[10px] font-bold text-red-600/70 uppercase tracking-wider mt-1">No</span>
                                </div>
                                <div className="col-span-2 grid grid-cols-2 gap-3">
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col items-center justify-center text-center">
                                        <span className="text-xl font-bold text-gray-600">{naCount}</span>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">N/A</span>
                                    </div>
                                    <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 flex flex-col items-center justify-center text-center">
                                        <span className="text-xl font-bold text-blue-600">{completionPercentage}%</span>
                                        <span className="text-[10px] font-bold text-blue-600/70 uppercase tracking-wider mt-1">Complete</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-4xl p-0 overflow-hidden bg-transparent border-none shadow-none flex items-center justify-center">
                    <img
                        src={selectedImageUrl}
                        alt="Full size inspection photo"
                        className="w-full h-auto max-h-[85vh] object-contain rounded-2xl ring-1 ring-white/10 shadow-2xl"
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ViewInspection;
